import React, { useContext, useEffect } from 'react'
import { Button, Checkbox, MenuItem, makeStyles, shorthands, tokens, Text, Divider, Tooltip } from '@fluentui/react-components'
import { useNavigate, useParams } from 'react-router-dom'
import { DataContext } from '../../App'
import { t } from '../../i18n/i18n'
import { GridItem } from '../../components/common/grid/GridItem'
import { Modal } from '../../components/common/modal/Modal'
import { useBoolean } from '@fluentui/react-hooks'
import { Grid } from '../../components/common/grid/Grid'
import { ControlledTextInput } from '../../components/common/inputs/TextInput'
import { useForm } from 'react-hook-form'
import { DemandComplianceModuleDefinition } from '../../api/schemas/schema'
import { Page } from '../Page'
import { BasicBreadcrumb } from '../../components/common/breadcrumb/BasicBreadcrumb'
import { setLoading } from '../../helpers/stateHelper'

import { useMsal } from '@azure/msal-react'
import { ContextualMenu } from '../../components/common/menu/Menu'
import { MoreHorizontal20Regular, Delete20Regular } from '@fluentui/react-icons'
import { Loader } from '../../components/common/spinner/Loader'

import { createDemandComplianceModule, deleteDemandComplianceModule, updateDemandComplianceModule } from '../../helpers/demandModuleHelper'

const useStyles = makeStyles({
   mTmB: { ...shorthands.margin(tokens.spacingVerticalXL, 'auto') },
   marginRight: {
      marginRight: tokens.spacingHorizontalXL,
   },
   marginTop: {
      marginTop: tokens.spacingVerticalM,
   },
   rowReverse: { display: 'flex', flexDirection: 'row-reverse' },
   flexEnd: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center' },
   limitWidth: {
      maxWidth: '90vw',
      margin: '0 auto',
   },
})
const DemandComplianceModuleDefinitionForm = () => {
   const { id } = useParams()
   const dataContext = useContext(DataContext)
   const { demandComplianceModuleDefinitions, isLoading } = dataContext.state

   const [open, { setTrue: openModal, setFalse: closeModal }] = useBoolean(false)
   const classes = useStyles()
   const demandForm = useForm<DemandComplianceModuleDefinition>()
   const navigate = useNavigate()
   const { accounts } = useMsal()
   const {
      watch,
      reset,
      control,
      formState: { isDirty },
      handleSubmit,
      setValue,
   } = demandForm

   const isGoodPractice = watch('systemCode') === 'GOOD_PRACTICE'

   const onUndoClick = () => {
      if (id === 'new') {
         reset()
      } else {
         reset(demandComplianceModuleDefinitions.find((x) => x.id === id))
      }
      closeModal()
   }
   const onSubmit = async (data: DemandComplianceModuleDefinition) => {
      setLoading(dataContext.setRootState, true)
      data.modifiedBy = accounts[0].name
      data.createdBy = accounts[0].name
      if (!watch('id')) {
         await createDemandComplianceModule(data, dataContext, reset, navigate)
      } else {
         await updateDemandComplianceModule(data, dataContext, reset)
      }
   }
   useEffect(() => {
      if (demandComplianceModuleDefinitions && id !== watch('id')) {
         if (id === 'new' && !watch('id')) {
            reset()
         } else {
            reset(demandComplianceModuleDefinitions.find((x) => x.id === id))
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [demandComplianceModuleDefinitions, id, watch('id')])

   return (
      <>
         <Page
            header={
               <GridItem size="12/12">
                  <BasicBreadcrumb
                     breadcrumbItems={[
                        { text: t('SystemSettings'), path: `/system-settings` },
                        { text: t('DemandComplianceAdditionalModules'), path: `/system-settings` },
                        {
                           text: watch('id') ? demandComplianceModuleDefinitions.find((x) => x.id === watch('id'))?.name ?? watch('name') : t('New'),
                           path: window.location.pathname,
                        },
                     ]}
                  />
               </GridItem>
            }
         >
            {demandComplianceModuleDefinitions === null ? (
               <Loader />
            ) : (
               <form onSubmit={handleSubmit(onSubmit)} className={classes.limitWidth}>
                  <div className={classes.rowReverse}>
                     <ContextualMenu
                        menuTrigger={<Button appearance="transparent" icon={<MoreHorizontal20Regular />} aria-label="More options" />}
                        menuItems={
                           <Tooltip content={t('GoodPracticeCantBeDeleted')} relationship={'label'} showDelay={300}>
                              <MenuItem
                                 icon={<Delete20Regular />}
                                 onClick={() => deleteDemandComplianceModule(dataContext, watch(), navigate)}
                                 disabled={isGoodPractice}
                              >
                                 {t('Delete')}
                              </MenuItem>
                           </Tooltip>
                        }
                     />
                  </div>
                  <Grid>
                     {/* DemandComplianceModule */}
                     <GridItem size="12/12">
                        <Text size={500} weight="semibold">
                           {t('DemandComplianceModule')}
                        </Text>
                        <Grid className={classes.marginTop}>
                           <GridItem size="8/12">
                              <ControlledTextInput control={control} name={'name'} label={t('Name')} required={true} />
                              <ControlledTextInput control={control} name={'infoLabel'} label={t('Info')} multiline={true} rows={5} />
                           </GridItem>
                           <GridItem size="4/12">
                              <ControlledTextInput control={control} name={'order'} label={t('Order')} required={true} type="number" />
                              <Checkbox
                                 label={t('Active')}
                                 checked={watch('active') || false}
                                 onChange={() => setValue('active', !watch('active'), { shouldDirty: true })}
                              />
                           </GridItem>
                        </Grid>
                     </GridItem>

                     <GridItem className={classes.flexEnd} size="12/12">
                        <Button className={classes.marginRight} disabled={!isDirty || isLoading} onClick={() => openModal()}>
                           {t('Undo')}
                        </Button>
                        <Button appearance="primary" type="submit" disabled={!isDirty || isLoading}>
                           {t('Save')}
                        </Button>
                     </GridItem>

                     <GridItem size="12/12">
                        <Divider />
                     </GridItem>

                     <Modal
                        open={open}
                        title={t('UndoChanges')}
                        actions={
                           <>
                              <Button onClick={() => closeModal()}>{t('Cancel')}</Button>
                              <Button onClick={() => onUndoClick()} appearance="primary">
                                 {t('Continue')}
                              </Button>
                           </>
                        }
                        children={t('UndoDemandComplianceModuleChanges')}
                     />
                  </Grid>
               </form>
            )}
         </Page>
      </>
   )
}

export default DemandComplianceModuleDefinitionForm
